/* ═══════════════════════════════════════════════════════════════════════════
   La machine à états de l'application.

   Sa raison d'être n'est pas l'organisation : c'est la RÈGLE DE COUPLAGE du
   plan (§3.7). React n'est monté que dans les écrans ; entrer en partie
   DÉMONTE l'arbre, ce qui ramène son coût runtime à exactement zéro pendant
   les 16,67 ms qui comptent.

   Le vrai piège que cette séparation évite n'est pas le choix du framework
   mais le PONT entre la boucle de jeu et l'interface : écrire dans l'état d'un
   framework depuis `requestAnimationFrame` coûte plus cher que tout le reste
   réuni. Ici, pendant PLAY, il n'y a rien à écrire — l'arbre n'existe pas.
   ═══════════════════════════════════════════════════════════════════════════ */

export const App = {
  Boot: 'BOOT',
  Title: 'TITLE',
  RunConfig: 'RUN_CONFIG',
  Loading: 'LOADING',
  Play: 'PLAY',
  Pause: 'PAUSE',
  /** Choix de carte après une montée de niveau. PAUSE DURE : la simulation est
   *  gelée, pas ralentie. Le plan est explicite — en navigateur, un ralenti
   *  ruine à la fois la lisibilité et le budget de frame. */
  Cards: 'CARDS',
  Summary: 'SUMMARY',
  Options: 'OPTIONS',
  /** Hangar : arbre de compétences et déblocages, entre deux runs. */
  Meta: 'META',
  /** Le REGISTRE DES MENACES : l'encyclopédie des ennemis, accessible depuis
   *  l'accueil. (Le nom d'état reste `Journal` pour ne pas casser les
   *  sauvegardes/replays qui n'en dépendent pas, mais l'écran est le Registre.) */
  Journal: 'JOURNAL',
  /** Entraînement : rejouer un secteur ou un boss déjà franchi. */
  Training: 'TRAINING',
  /** Menu DÉVELOPPEUR (depuis la pause) : téléportation aux niveaux, réglage du
   *  loadout, octroi d'améliorations, triche méta. Outil de dev, pas de jeu. */
  Dev: 'DEV',
  /** ATTRACT MODE : après une inactivité prolongée sur l'écran-titre, une partie
   *  de démonstration tourne DERRIÈRE le menu à 25 % d'opacité. C'est le seul
   *  état où la simulation tourne ET l'arbre React est monté — le menu sert de
   *  voile par-dessus le jeu qui se joue tout seul. */
  Attract: 'ATTRACT',
} as const;

export type AppStateId = (typeof App)[keyof typeof App];

/** États où la simulation tourne. Partout ailleurs, le monde est figé.
 *  ATTRACT en fait partie : la démo se joue réellement, elle n'est pas un film. */
const RUNNING = new Set<string>([App.Play, App.Attract]);

/** États où l'arbre React est monté. PLAY en est volontairement absent ; ATTRACT
 *  y est au contraire — c'est le menu, posé en voile sur la démo. */
const REACT_MOUNTED = new Set<string>([
  App.Title,
  App.RunConfig,
  App.Pause,
  App.Cards,
  App.Summary,
  App.Options,
  App.Meta,
  App.Journal,
  App.Training,
  App.Dev,
  App.Attract,
]);

/** Transitions autorisées. Une machine à états qui accepte tout n'est pas une
 *  machine à états : c'est la liste explicite qui empêche les états
 *  impossibles — « en pause pendant l'écran-titre », « cartes après la mort ». */
const ALLOWED: Readonly<Record<string, readonly AppStateId[]>> = {
  [App.Boot]: [App.Title],
  // TITLE → LOADING directement : l'écran de configuration de run n'existe pas
  // encore, et le chemin « jouer » ne doit pas dépendre d'un écran à venir.
  // TITLE → ATTRACT : la démo se déclenche sur inactivité. Elle en ressort par
  // TITLE au premier geste — jamais directement en partie, pour que le joueur
  // reparte toujours d'un menu net et interactif.
  [App.Title]: [App.RunConfig, App.Loading, App.Options, App.Meta, App.Journal, App.Attract],
  [App.Attract]: [App.Title],
  // Le hangar se quitte vers le titre ou directement vers une partie : après
  // avoir acheté une amélioration, la première envie est de l'essayer.
  // Le journal et l'entraînement s'ouvrent depuis le hangar : c'est déjà
  // l'écran « entre deux parties », et y accrocher ces deux-là évite d'allonger
  // l'écran-titre, dont la valeur tient à sa brièveté.
  [App.Meta]: [App.Title, App.Loading, App.Training],
  [App.Journal]: [App.Title],
  [App.Training]: [App.Meta, App.Loading],
  [App.RunConfig]: [App.Title, App.Loading],
  [App.Loading]: [App.Play],
  [App.Play]: [App.Pause, App.Cards, App.Summary],
  // PAUSE → SUMMARY : abandonner une partie mène au récapitulatif, pas
  // directement au titre — le joueur a droit à son bilan même s'il renonce.
  // PAUSE → DEV : le menu développeur s'ouvre depuis la pause.
  [App.Pause]: [App.Play, App.Summary, App.Title, App.Options, App.Dev],
  // Le menu dev revient à la pause/au jeu, relance une partie (via Loading) ou
  // rend la main au titre.
  [App.Dev]: [App.Pause, App.Play, App.Loading, App.Title],
  [App.Cards]: [App.Play, App.Summary],
  [App.Summary]: [App.Title, App.Loading, App.Meta],
  [App.Options]: [App.Title, App.Pause],
};

export class AppMachine {
  private current: AppStateId = App.Boot;
  /** Écran d'où l'on a ouvert les options, pour savoir où revenir. */
  private optionsFrom: AppStateId = App.Title;
  private listeners: Array<(to: AppStateId, from: AppStateId) => void> = [];

  get state(): AppStateId {
    return this.current;
  }

  get simRuns(): boolean {
    return RUNNING.has(this.current);
  }

  get reactMounted(): boolean {
    return REACT_MOUNTED.has(this.current);
  }

  can(to: AppStateId): boolean {
    return (ALLOWED[this.current] ?? []).includes(to);
  }

  /**
   * Retourne `false` si la transition est interdite — sans lever d'exception :
   * une entrée clavier parasite ne doit pas faire tomber le jeu.
   *
   * ⚠️ Mais un refus est SIGNALÉ. Une transition légitime refusée en silence
   * produit exactement ce qui est arrivé au J8 : le bouton « JOUER » ne faisait
   * rien, l'écran se redessinait à l'identique, et rien nulle part n'indiquait
   * pourquoi. Un garde-fou muet est pire qu'un garde-fou absent — il donne
   * l'illusion que le problème est ailleurs.
   */
  to(next: AppStateId): boolean {
    if (next === this.current) return true;
    if (!this.can(next)) {
      if (typeof console !== 'undefined') {
        console.warn(`[AppState] transition refusée : ${this.current} → ${next}`);
      }
      return false;
    }
    const from = this.current;
    if (next === App.Options) this.optionsFrom = from;
    this.current = next;
    for (const l of this.listeners) l(next, from);
    return true;
  }

  /** Sortie des options : retour à l'écran d'origine. */
  closeOptions(): boolean {
    return this.to(this.optionsFrom);
  }

  onChange(fn: (to: AppStateId, from: AppStateId) => void): () => void {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }
}

/** Chemin le plus court de la mort au run suivant.
 *
 *  Critère du jalon : « mort → nouveau run en ≤ 3 entrées et ≤ 4 s »
 *  (PLAN.md §6.12). Un jeu où relancer demande six clics fait abandonner bien
 *  avant que sa difficulté n'y soit pour quelque chose. */
export const REPLAY_PATH: readonly AppStateId[] = [App.Summary, App.Loading, App.Play];
